import { useState, useEffect, useRef } from 'react';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('animate-in');
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const headerRef = useScrollReveal();
  const bodyRef = useScrollReveal(0.1);

  const testimonials = [
    {
      id: 1,
      text: 'Best surf school experience ever! The instructors were patient, knowledgeable, and made learning so much fun. I went from never standing on a board to catching waves on my own in just three days. Essaouira is absolutely magical!',
      name: 'Sarah Johnson',
      location: 'London, UK',
      date: 'March 2024',
      image: 'https://public.readdy.ai/ai/img_res/94ab06b0f8a9fd7f38da9926e80baa59.jpg'
    },
    {
      id: 2,
      text: 'The kitesurf lessons exceeded all my expectations. Professional equipment, expert instruction, and the perfect location. The team really knows the local conditions and made sure I progressed safely. Can\'t wait to come back next year!',
      name: 'Marco Rossi',
      location: 'Milan, Italy',
      date: 'February 2024',
      image: 'https://readdy.ai/api/search-image?query=Smiling%20man%20in%20wetsuit%20holding%20kite%20equipment%20on%20beach%20in%20Essaouira%20Morocco%20looking%20happy%20and%20confident%2C%20authentic%20portrait%20of%20satisfied%20kitesurfing%20student%20with%20ocean%20and%20blue%20sky%20background%2C%20energetic%20outdoor%20sports%20photography%20showing%20success%20and%20joy%2C%20natural%20lighting%20with%20vibrant%20colors&width=600&height=800&seq=testimonial-man-kitesurf-002&orientation=portrait'
    },
    {
      id: 3,
      text: 'As a complete beginner, I was nervous about trying surfing. The instructors made me feel comfortable from day one. Small group sizes meant lots of personal attention. By the end of the week, I was hooked! This place is paradise for water sports.',
      name: 'Emma Schmidt',
      location: 'Berlin, Germany',
      date: 'January 2024',
      image: 'https://readdy.ai/api/search-image?query=Cheerful%20young%20woman%20in%20wetsuit%20on%20beach%20in%20Essaouira%20Morocco%20with%20surfboard%20giving%20thumbs%20up%2C%20authentic%20happy%20portrait%20of%20beginner%20surf%20student%20showing%20confidence%2C%20turquoise%20waves%20and%20sandy%20beach%20background%2C%20warm%20sunny%20outdoor%20photography%20with%20natural%20smile%20and%20positive%20energy&width=600&height=800&seq=testimonial-woman-wetsuit-003&orientation=portrait'
    }
  ];

  const nextTestimonial = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  const current = testimonials[currentIndex];

  return (
    <section className="py-24 bg-sand-200 relative overflow-hidden">
      <style>{`
        .reveal-fade { opacity: 0; transform: translateY(30px); transition: opacity 0.75s ease, transform 0.75s ease; }
        .reveal-fade.animate-in { opacity: 1; transform: translateY(0); }
        .reveal-fade-delay { opacity: 0; transform: translateY(30px); transition: opacity 0.75s ease 0.2s, transform 0.75s ease 0.2s; }
        .reveal-fade-delay.animate-in { opacity: 1; transform: translateY(0); }
      `}</style>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div ref={headerRef} className="reveal-fade text-center mb-16">
          <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-3">Témoignages</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
            Ce que disent nos élèves
          </h2>
          <p className="text-base text-gray-500">
            Des expériences authentiques, des sourires réels
          </p>
        </div>

        <div ref={bodyRef} className="reveal-fade-delay grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Portrait Image */}
          <div className="lg:col-span-4">
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-96 lg:h-[480px] w-full">
              <img
                src={current.image}
                alt={current.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Testimonial Card */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-sand-200">
              <div className="text-teal-400 mb-4">
                <i className="ri-double-quotes-l text-6xl opacity-40"></i>
              </div>
              <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-8 font-light">
                {current.text}
              </p>
              <div className="mb-8">
                <p className="text-lg font-bold text-gray-900 mb-1">{current.name}</p>
                <p className="text-sm text-gray-400">{current.location} · {current.date}</p>
              </div>
              <div className="flex items-center gap-4">
                <button onClick={prevTestimonial} className="w-11 h-11 flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-teal-600 transition-all cursor-pointer">
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <button onClick={nextTestimonial} className="w-11 h-11 flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-teal-600 transition-all cursor-pointer">
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
                <div className="flex-1 flex items-center gap-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`h-1.5 rounded-full transition-all cursor-pointer ${index === currentIndex ? 'w-8 bg-teal-500' : 'w-2 bg-sand-300'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
